import React from "react";
import { ResourceType } from "../../types/resource";
import { useResourceStore } from "../../store/resourceStore";
import { useOutlookActions } from "../../hooks/useOutlookActions";
import { useToastStore } from "../../store/toastStore";
import { useOfficeSync } from "../../hooks/useOfficeSync";

/**
 * Resource Service - Tầng trung gian xử lý logic nghiệp vụ cho resource management
 * 
 * Logic nghiệp vụ:
 * 1. Nhận toggle resource từ UI
 * 2. Xử lý điều kiện nghiệp vụ
 * 3. Cập nhật state store nếu thỏa mãn điều kiện
 * 4. Đồng bộ với Outlook API
 * 5. Rollback nếu có lỗi
 */

// Helper function to normalize email format for Outlook
const normalizeEmailForOutlook = (input: string): string => {
  if (!input) return "unknown@company.com";
  
  // If already a valid email, return as is
  if (input.includes("@") && input.includes(".")) {
    return input.toLowerCase().trim();
  }
  
  // Convert name/path to proper email format
  const cleanName = input
    .toLowerCase()
    .replace(/\s+/g, "") // Remove spaces
    .replace(/[^a-z0-9]/g, "") // Remove special characters
    .replace(/\//g, "") // Remove slashes from paths
    .trim();
  
  // Ensure we have a valid identifier
  const finalName = cleanName || "resource";
  
  return `${finalName}@company.com`;
};

export interface ResourceServiceResult {
  success: boolean;
  message: string;
  resource?: ResourceType;
}

/**
 * Hook để sử dụng ResourceService
 */
export const useResourceService = () => {
  const {
    selectedResource,
    // ✅ Removed: addSelectedResource, removeSelectedResource, clearSelectedResources
    // We now use OUTLOOK-FIRST approach - only call Outlook API, let useOfficeEvents sync store
  } = useResourceStore();
  
  const { AddResources, RemoveResources } = useOutlookActions();
  const { showToast } = useToastStore();
  const { isSyncInProgress } = useOfficeSync();

  // Flag để track khi đang xử lý operations để tránh conflict với events
  const [isProcessing, setIsProcessing] = React.useState(false);

  /**
   * Kiểm tra điều kiện nghiệp vụ trước khi xử lý
   */
  const validateResource = (resource: ResourceType): ResourceServiceResult => {
    // ✅ Check for sync conflicts first
    if (isSyncInProgress()) {
      console.warn("⚠️ ResourceService: Office sync in progress, deferring operation");
      return {
        success: false,
        message: "Outlook sync in progress, please try again in a moment",
        resource
      };
    }

    // Kiểm tra resource hợp lệ
    if (!resource.Email || !resource.Name) {
      return {
        success: false,
        message: "Invalid resource: Missing email or name",
        resource
      };
    }

    // Kiểm tra resource có available không (có thể bỏ qua tùy business logic)
    // if (!resource.IsAvailable) {
    //   return {
    //     success: false,
    //     message: `${resource.Name} is not available`,
    //     resource
    //   };
    // }

    // Kiểm tra giới hạn số lượng resource được chọn (ví dụ: tối đa 5)
    const currentSelectedCount = selectedResource.length;
    const isCurrentlySelected = isResourceSelected(resource);
    
    if (!isCurrentlySelected && currentSelectedCount >= 5) {
      return {
        success: false,
        message: "Maximum 5 resources can be selected",
        resource
      };
    }

    // Có thể thêm các điều kiện khác tùy theo business logic:
    // - Kiểm tra conflict thời gian
    // - Kiểm tra quyền truy cập
    // - Kiểm tra policy của công ty
    // - etc.

    return {
      success: true,
      message: "Validation passed",
      resource
    };
  };

  /**
   * Kiểm tra xem resource đã được chọn chưa
   */
  const isResourceSelected = (resource: ResourceType): boolean => {
    return selectedResource.some(selected => 
      selected.Email.toLowerCase() === resource.Email.toLowerCase() ||
      selected.ID === resource.ID
    );
  };

  /**
   * Thêm resource mới - OUTLOOK-FIRST approach (no direct store manipulation)
   */
  const addResource = async (resource: ResourceType): Promise<ResourceServiceResult> => {
    console.log("➕ ResourceService: Adding resource to Outlook (store will sync automatically)", resource.Name);

    try {
      setIsProcessing(true);
      
      // ✅ ONLY call Outlook API - let useOfficeEvents handle store updates
      const outlookSuccess = await AddResources([resource]);

      if (!outlookSuccess) {
        const errorMessage = `Failed to add ${resource.Name} to Outlook appointment`;
        showToast(errorMessage, false);
        return {
          success: false,
          message: errorMessage,
          resource
        };
      }

      // ✅ Wait for useOfficeEvents to sync the change (no manual store updates)
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const successMessage = `${resource.Name} added successfully!`;
      showToast(successMessage, true);
      console.log("✅ ResourceService: Resource added to Outlook, store will sync automatically");
      
      return {
        success: true,
        message: successMessage,
        resource
      };

    } catch (error) {
      console.error("❌ ResourceService: Add resource failed", error);
      
      const errorMessage = `Failed to add ${resource.Name}`;
      showToast(errorMessage, false);
      return {
        success: false,
        message: errorMessage,
        resource
      };
    } finally {
      setIsProcessing(false);
    }
  };

    /**
   * Xóa resource - OUTLOOK-FIRST approach (no direct store manipulation)
   */
  const removeResource = async (resource: ResourceType): Promise<ResourceServiceResult> => {
    console.log("➖ ResourceService: Removing resource from Outlook (store will sync automatically)", resource.Name);

    try {
      setIsProcessing(true);
      
      // ✅ ONLY call Outlook API - let useOfficeEvents handle store updates
      const outlookSuccess = await RemoveResources([resource]);

      if (!outlookSuccess) {
        const errorMessage = `Failed to remove ${resource.Name} from Outlook appointment`;
        showToast(errorMessage, false);
        return {
          success: false,
          message: errorMessage,
          resource
        };
      }

      // ✅ Wait for useOfficeEvents to sync the change (no manual store updates)
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const successMessage = `${resource.Name} removed successfully!`;
      showToast(successMessage, true);
      console.log("✅ ResourceService: Resource removed from Outlook, store will sync automatically");
      
      return {
        success: true,
        message: successMessage,
        resource
      };

    } catch (error) {
      console.error("❌ ResourceService: Remove resource failed", error);
      
      const errorMessage = `Failed to remove ${resource.Name}`;
      showToast(errorMessage, false);
      return {
        success: false,
        message: errorMessage,
        resource
      };
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Toggle resource selection với logic nghiệp vụ
   */
  const toggleResource = async (resource: ResourceType): Promise<ResourceServiceResult> => {
    try {
      console.log("🔄 ResourceService: Toggle resource called", resource.Name);

      // Normalize email format để ensure consistency
      const normalizedResource = {
        ...resource,
        Email: normalizeEmailForOutlook(resource.Email || resource.Name),
        Location: resource.Location || resource.Name || "Meeting Room"
      };

      console.log("📧 ResourceService: Normalized resource:", {
        name: normalizedResource.Name,
        email: normalizedResource.Email,
        location: normalizedResource.Location
      });

      // Kiểm tra điều kiện nghiệp vụ trước khi xử lý
      const validationResult = validateResource(normalizedResource);
      if (!validationResult.success) {
        showToast(validationResult.message, false);
        return validationResult;
      }

      // Kiểm tra xem resource đã được chọn chưa
      const isCurrentlySelected = isResourceSelected(normalizedResource);

      if (isCurrentlySelected) {
        return await removeResource(normalizedResource);
      } else {
        return await addResource(normalizedResource);
      }
    } catch (error) {
      console.error("❌ ResourceService: Toggle resource failed", error);
      const errorMessage = `Failed to process resource: ${resource.Name}`;
      showToast(errorMessage, false);
      return {
        success: false,
        message: errorMessage,
        resource
      };
    }
  };

  /**
   * Batch operation: Thêm nhiều resources cùng lúc
   */
  const addMultipleResources = async (resources: ResourceType[]): Promise<ResourceServiceResult[]> => {
    console.log("📦 ResourceService: Adding multiple resources", resources.length);
    
    const results: ResourceServiceResult[] = [];
    
    for (const resource of resources) {
      if (!isResourceSelected(resource)) {
        const result = await addResource(resource);
        results.push(result);
      }
    }
    
    return results;
  };

  /**
   * Batch operation: Xóa nhiều resources cùng lúc
   */
  const removeMultipleResources = async (resources: ResourceType[]): Promise<ResourceServiceResult[]> => {
    console.log("📦 ResourceService: Removing multiple resources", resources.length);
    
    const results: ResourceServiceResult[] = [];
    
    for (const resource of resources) {
      if (isResourceSelected(resource)) {
        const result = await removeResource(resource);
        results.push(result);
      }
    }
    
    return results;
  };

  /**
   * Clear all selected resources - OUTLOOK-FIRST approach
   */
  const clearAllResources = async (): Promise<ResourceServiceResult> => {
    console.log("🧹 ResourceService: Clearing all resources from Outlook (store will sync automatically)");
    
    try {
      setIsProcessing(true);
      const selectedResources = [...selectedResource];
      
      if (selectedResources.length === 0) {
        return {
          success: true,
          message: "No resources to clear"
        };
      }

      // ✅ ONLY call Outlook API - let useOfficeEvents handle store updates
      const outlookSuccess = await RemoveResources(selectedResources);
      
      if (!outlookSuccess) {
        throw new Error("Failed to remove resources from Outlook");
      }

      // ✅ Wait for useOfficeEvents to sync the change (no manual store updates)
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const successMessage = `Cleared ${selectedResources.length} resources successfully!`;
      showToast(successMessage, true);
      console.log("✅ ResourceService: All resources cleared from Outlook, store will sync automatically");
      
      return {
        success: true,
        message: successMessage
      };

    } catch (error) {
      console.error("❌ ResourceService: Clear all failed", error);
      const errorMessage = "Failed to clear all resources";
      showToast(errorMessage, false);
      
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    toggleResource,
    addMultipleResources,
    removeMultipleResources,
    clearAllResources,
    isResourceSelected,
    isProcessing, // Export processing state
  };
};